/**
 * FormFieldEditor.tsx — editor de campos del constructor de formularios.
 * Edición local (mismo criterio que PipelineEditorModal: se compone en
 * memoria acá, el padre persiste con un solo save) — filas con label, tipo,
 * obligatorio, opciones (select/multiselect) y reorden arriba/abajo. Compone
 * únicamente primitives existentes (Input, Dropdown, Toggle, Badge,
 * IconButton, Button) — sin primitive nuevo, sin token nuevo.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FORM_FIELD_TYPES, FORM_FIELD_TYPES_WITH_OPTIONS, FORM_FIELD_TYPES_WITH_HELP_TEXT, type FormFieldType, type FormFieldWidth } from '@evo-hub/shared';
import { Input } from '../ui/Input.js';
import { Dropdown } from '../ui/Dropdown.js';
import { SegmentedControl } from '../ui/SegmentedControl.js';
import { Toggle } from '../ui/Toggle.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Icon } from '../Icon.js';
import type { FieldInput } from '../../hooks/useForms.js';

interface Props {
  fields: FieldInput[];
  onChange: (fields: FieldInput[]) => void;
}

function emptyField(): FieldInput {
  return { label: '', fieldType: 'text', required: false, options: [], placeholder: '', width: 'full' };
}

export function FormFieldEditor({ fields, onChange }: Props) {
  const { t } = useTranslation();

  function update(i: number, patch: Partial<FieldInput>) {
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  function remove(i: number) {
    onChange(fields.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
    onChange(next);
  }
  function addField() {
    onChange([...fields, emptyField()]);
  }
  function addOption(i: number, raw: string) {
    const value = raw.trim();
    if (!value) return;
    const f = fields[i]!;
    update(i, { options: [...(f.options ?? []), value] });
  }
  function removeOption(i: number, optIdx: number) {
    const f = fields[i]!;
    update(i, { options: (f.options ?? []).filter((_, idx) => idx !== optIdx) });
  }

  const typeOptions = FORM_FIELD_TYPES.map(type => ({ value: type, label: t(`forms.fields.type.${type}`) }));
  const widthOptions: { value: FormFieldWidth; label: string }[] = [
    { value: 'full', label: t('forms.fields.width.full') },
    { value: 'half', label: t('forms.fields.width.half') },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      {fields.length === 0 && (
        <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>{t('forms.fields.empty')}</p>
      )}
      {fields.map((f, i) => {
        const hasOptions = FORM_FIELD_TYPES_WITH_OPTIONS.has(f.fieldType);
        // En checkbox el placeholder se muestra como texto de ayuda debajo de
        // la casilla — mismo campo, distinto rol, así que distinto rótulo.
        const usesHelpText = FORM_FIELD_TYPES_WITH_HELP_TEXT.has(f.fieldType);
        return (
          <div
            key={i}
            style={{
              border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)',
              padding: 'var(--evo-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)',
              background: 'var(--evo-bg-2)',
            }}
          >
            <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Input
                  type="text"
                  size="sm"
                  fullWidth
                  value={f.label}
                  onChange={v => update(i, { label: v })}
                  placeholder={t('forms.fields.labelPlaceholder')}
                />
              </div>
              <div style={{ width: '180px' }}>
                <Dropdown<FormFieldType>
                  value={f.fieldType}
                  options={typeOptions}
                  onChange={type => update(i, { fieldType: type, options: FORM_FIELD_TYPES_WITH_OPTIONS.has(type) ? (f.options ?? []) : [] })}
                  size="sm"
                />
              </div>
              {/* DS-EXCEPTION: no hay chevron-up/down dedicados — chevron-left rotado ±90° lee como arriba/abajo en este reorden vertical. */}
              <div style={{ display: 'flex', gap: '4px', flexShrink: 0, paddingTop: '4px' }}>
                <IconButton
                  icon={<span style={{ display: 'inline-flex', transform: 'rotate(90deg)' }}><Icon name="chevron-left" size={14} /></span>} ariaLabel={t('forms.fields.moveUp')}
                  variant="ghost" tone="muted" size="sm"
                  onClick={() => move(i, -1)} disabled={i === 0} tooltip={t('forms.fields.moveUp')}
                />
                <IconButton
                  icon={<span style={{ display: 'inline-flex', transform: 'rotate(-90deg)' }}><Icon name="chevron-left" size={14} /></span>} ariaLabel={t('forms.fields.moveDown')}
                  variant="ghost" tone="muted" size="sm"
                  onClick={() => move(i, 1)} disabled={i === fields.length - 1} tooltip={t('forms.fields.moveDown')}
                />
                <IconButton icon={<Icon name="close" size={14} />} ariaLabel={t('forms.fields.removeField')} variant="ghost" tone="danger" size="sm"
                  onClick={() => remove(i)}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--evo-space-4)', alignItems: 'center' }}>
              <Toggle checked={!!f.required} onChange={v => update(i, { required: v })} label={t('forms.fields.requiredLabel')} size="sm" />
              <div style={{ flex: 1 }}>
                <Input
                  type="text"
                  size="sm"
                  fullWidth
                  value={f.placeholder ?? ''}
                  onChange={v => update(i, { placeholder: v })}
                  placeholder={usesHelpText ? t('forms.fields.helpTextLabel') : t('forms.fields.placeholderLabel')}
                />
              </div>
              {/* Ancho en el formulario público: dos campos "mitad"
                  consecutivos se acomodan lado a lado. */}
              <SegmentedControl<FormFieldWidth>
                value={f.width ?? 'full'}
                options={widthOptions}
                onChange={w => update(i, { width: w })}
                size="sm"
              />
            </div>

            {hasOptions && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--evo-fg-3)' }}>{t('forms.fields.optionsLabel')}</span>
                {(f.options ?? []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
                    {(f.options ?? []).map((opt, optIdx) => (
                      <Badge key={optIdx} variant="soft" tone="cyan" size="sm"
                        rightIcon={<Icon name="close" size={9} />}
                        onClick={() => removeOption(i, optIdx)}
                      >
                        {opt}
                      </Badge>
                    ))}
                  </div>
                )}
                <OptionAdder onAdd={value => addOption(i, value)} placeholder={t('forms.fields.optionPlaceholder')} addLabel={t('forms.fields.addOption')} />
              </div>
            )}
          </div>
        );
      })}

      <div>
        <Button type="button" variant="outline" tone="muted" size="sm" onClick={addField}>
          {t('forms.fields.addBtn')}
        </Button>
      </div>
    </div>
  );
}

function OptionAdder({ onAdd, placeholder, addLabel }: { onAdd: (value: string) => void; placeholder: string; addLabel: string }) {
  const [value, setValue] = useState('');
  function commit() {
    if (!value.trim()) return;
    onAdd(value);
    setValue('');
  }
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <Input
        type="text"
        size="sm"
        value={value}
        onChange={setValue}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        placeholder={placeholder}
      />
      <Button type="button" variant="outline" tone="muted" size="sm" onClick={commit}>{addLabel}</Button>
    </div>
  );
}
